import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar as CalendarUI } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { ArrowLeft, Upload, X, MapPin, Search, Calendar as CalendarIcon } from 'lucide-react';
import { useGoogleMaps, initializeMap, createMarker, createSearchBox, reverseGeocode } from '../../hooks/useGoogleMaps';
import { useCalendarHolidays } from '../../hooks/useHolidays';

const CreateProject = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [oems, setOems] = useState([]);
    const [cities, setCities] = useState([]);
    const [entities, setEntities] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const mapLoaded = useGoogleMaps(); // Use the custom hook
    const [map, setMap] = useState(null);
    const markerRef = useRef(null); // Use ref to properly track marker
    const fileInputRef = useRef(null); // Reference to file input for resetting
    const [searchBox, setSearchBox] = useState(null);
    const [mapType, setMapType] = useState('roadmap'); // 'roadmap' or 'satellite'
    const entitySectionRef = useRef(null); // Reference to entity section for scrolling
    const { isHoliday, getHolidayName, holidayDates, onMonthChange: onHolidayMonthChange } = useCalendarHolidays();
    const documentsSectionRef = useRef(null); // Reference to documents section for scrolling
    const timelineSectionRef = useRef(null); // Reference to timeline section for scrolling
    const [startDateOpen, setStartDateOpen] = useState(false);
    const [completionDateOpen, setCompletionDateOpen] = useState(false);
    const [startCalendarMonth, setStartCalendarMonth] = useState(new Date());
    const [completionCalendarMonth, setCompletionCalendarMonth] = useState(new Date());

    const [projectData, setProjectData] = useState({
        oemId: '',
        clientName: '',
        clientPhone: '',
        siteAddress: '',
        cityId: '',
        pincode: '',
        sqft: '',
        latitude: '',
        longitude: '',
        preferredStartDate: new Date().toISOString().split('T')[0],
        preferredCompletionDate: '',
        hasExtraWork: false,
        extraWorkNotes: '',
        selectedEntities: []
    });

    // Calculate estimated days
    const projectEstimatedDays = React.useMemo(() => {
        if (projectData.preferredStartDate && projectData.preferredCompletionDate) {
            const start = new Date(projectData.preferredStartDate);
            const end = new Date(projectData.preferredCompletionDate);
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays > 0 ? diffDays : 0;
        }
        return 0;
    }, [projectData.preferredStartDate, projectData.preferredCompletionDate]);

    // Initialize map when Google Maps is loaded
    useEffect(() => {
        if (mapLoaded && !map) {
            setupMap();
        }
    }, [mapLoaded]);

    // Don't auto-detect location - user will click button instead

    const getUserLocation = useCallback(() => {
        if (!map) return;
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser');
            return;
        }
        if (!window.isSecureContext) {
            toast.error('Live location requires HTTPS. Please open this site over HTTPS or use localhost.');
            return;
        }
        const loadingToast = toast.loading('Detecting your location...');
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                toast.dismiss(loadingToast);
                const userLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                // Center map on user location
                map.setCenter(userLocation);
                map.setZoom(15);

                // Create marker at user location
                const latLng = new window.google.maps.LatLng(userLocation.lat, userLocation.lng);
                updateMarker(map, latLng);

                // Get address for user location
                try {
                    const address = await reverseGeocode(latLng);
                    setProjectData(prev => ({
                        ...prev,
                        siteAddress: address
                    }));
                } catch (error) {
                    console.error('Geocoding error:', error);
                }
            },
            (error) => {
                toast.dismiss(loadingToast);
                console.log('Geolocation error:', error);
                let msg = 'Could not detect your location';
                if (error.code === 1) msg = 'Location permission denied. Enable it in Settings → Safari → Location.';
                else if (error.code === 2) msg = 'Location unavailable. Check device GPS / network.';
                else if (error.code === 3) msg = 'Location request timed out. Try again.';
                toast.error(msg);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    }, [map]);

    const setupMap = useCallback(() => {
        // Initialize the map
        const newMap = initializeMap('project-map');
        if (!newMap) return;

        // Set initial map type
        newMap.setMapTypeId(mapType);

        // Set up search box
        const input = document.getElementById('location-search');
        const newSearchBox = createSearchBox(input, newMap);
        if (!newSearchBox) return;

        // Listen for search box selection
        newSearchBox.addListener('places_changed', () => {
            const places = newSearchBox.getPlaces();
            if (places.length === 0) return;

            const place = places[0];
            if (!place.geometry || !place.geometry.location) return;

            // Update map
            newMap.setCenter(place.geometry.location);
            newMap.setZoom(15);

            // Update marker
            updateMarker(newMap, place.geometry.location, place.formatted_address);
        });

        // Click on map to set location
        newMap.addListener('click', async (e) => {
            updateMarker(newMap, e.latLng);

            // Reverse geocode to get address
            try {
                const address = await reverseGeocode(e.latLng);
                setProjectData(prev => ({
                    ...prev,
                    siteAddress: address
                }));
            } catch (error) {
                console.error('Geocoding error:', error);
            }
        });

        setMap(newMap);
        setSearchBox(newSearchBox);
    }, [mapType]);

    const updateMarker = (mapInstance, location, address = null) => {
        // Remove existing marker if it exists
        if (markerRef.current) {
            markerRef.current.setMap(null);
        }

        // Create new marker using utility function
        const newMarker = createMarker(mapInstance, location);

        // Update coordinates
        setProjectData(prev => ({
            ...prev,
            latitude: location.lat().toFixed(6),
            longitude: location.lng().toFixed(6),
            ...(address && { siteAddress: address })
        }));

        // Handle marker drag
        newMarker.addListener('dragend', async (e) => {
            const newPos = e.latLng;
            setProjectData(prev => ({
                ...prev,
                latitude: newPos.lat().toFixed(6),
                longitude: newPos.lng().toFixed(6)
            }));

            // Reverse geocode
            try {
                const address = await reverseGeocode(newPos);
                setProjectData(prev => ({
                    ...prev,
                    siteAddress: address
                }));
            } catch (error) {
                console.error('Geocoding error:', error);
            }
        });

        markerRef.current = newMarker;
    };

    // Clear location
    const handleClearLocation = () => {
        if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
        }
        setProjectData(prev => ({
            ...prev,
            latitude: '',
            longitude: '',
            siteAddress: ''
        }));

        // Reset map to India center
        if (map) {
            map.setCenter({ lat: 20.5937, lng: 78.9629 });
            map.setZoom(5);
        }
    };

    // Toggle map type between roadmap and satellite
    const toggleMapType = () => {
        const newType = mapType === 'roadmap' ? 'satellite' : 'roadmap';
        setMapType(newType);
        if (map) {
            map.setMapTypeId(newType);
        }
    };

    // Fetch initial data
    useEffect(() => {
        fetchOems();
        fetchCities();
        fetchEntities();

        // If user is OEM, fetch their full details to get OEM ID
        if (user?.role?.toLowerCase() === 'oem') {
            fetchCurrentUserDetails();
        }
    }, []);

    const fetchOems = async () => {
        try {
            const response = await api.getOems();
            console.log('OEMs API Response:', response);
            // Handle both camelCase (items) and PascalCase (Items)
            const oemsData = Array.isArray(response?.items) ? response.items :
                Array.isArray(response?.Items) ? response.Items : [];
            console.log('OEMs Data:', oemsData); if (oemsData.length > 0) {
                console.log('First OEM object:', oemsData[0]);
            } setOems(oemsData);
        } catch (error) {
            console.error('Error fetching OEMs:', error);
            toast.error('Failed to load OEMs');
            setOems([]);
        }
    };

    const fetchCurrentUserDetails = async () => {
        try {
            const currentUser = await api.getCurrentUser();
            // If user is OEM and has OEM data, auto-populate the OEM field
            if (currentUser?.oem?.id) {
                setProjectData(prev => ({ ...prev, oemId: currentUser.oem.id }));
            }
        } catch (error) {
            console.error('Error fetching current user details:', error);
        }
    };

    const fetchCities = async () => {
        try {
            const response = await api.getCities();
            setCities(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Error fetching cities:', error);
            toast.error('Failed to load cities');
            setCities([]);
        }
    };

    const fetchEntities = async () => {
        try {
            const response = await api.getEntities();
            console.log('Entities API Response:', response);
            // Handle both camelCase (items) and PascalCase (Items)
            const entitiesData = Array.isArray(response?.items) ? response.items :
                Array.isArray(response?.Items) ? response.Items : [];
            console.log('Entities Data:', entitiesData);
            if (entitiesData.length > 0) {
                console.log('First Entity object:', entitiesData[0]);
            }
            setEntities(entitiesData);
        } catch (error) {
            console.error('Error fetching entities:', error);
            toast.error('Failed to load entities');
            setEntities([]);
        }
    };

    const toggleEntity = (entityId) => {
        setProjectData(prev => {
            const existing = prev.selectedEntities.find(e => e.entityId === entityId);
            if (existing) {
                return {
                    ...prev,
                    selectedEntities: prev.selectedEntities.filter(e => e.entityId !== entityId)
                };
            } else {
                return {
                    ...prev,
                    selectedEntities: [...prev.selectedEntities, { entityId, quantity: 1 }]
                };
            }
        });
    };

    const updateEntityQuantity = (entityId, quantity) => {
        setProjectData(prev => ({
            ...prev,
            selectedEntities: prev.selectedEntities.map(e =>
                e.entityId === entityId ? { ...e, quantity: parseInt(quantity) || 1 } : e
            )
        }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        // Validation rules from database
        const allowedMimeTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/bmp',
            'image/svg+xml'
        ];
        const maxFileSizeMB = 10;
        const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

        // Validate each file
        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
            // Check file size
            if (file.size > maxFileSizeBytes) {
                invalidFiles.push(`${file.name} exceeds ${maxFileSizeMB}MB limit`);
                return;
            }

            // Check file type
            if (!allowedMimeTypes.includes(file.type)) {
                invalidFiles.push(`${file.name} has invalid type (${file.type})`);
                return;
            }

            validFiles.push(file);
        });

        // Show errors if any
        if (invalidFiles.length > 0) {
            toast({
                title: "File Validation Errors",
                description: invalidFiles.join('\n'),
                variant: "destructive"
            });
        }

        // Add only valid files with sub-category tracking (default to 'other')
        if (validFiles.length > 0) {
            const filesWithCategory = validFiles.map(file => ({
                file,
                subCategory: 'other'
            }));
            setUploadedFiles(prev => [...prev, ...filesWithCategory]);
        }
    };

    const removeFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!projectData.oemId) {
            toast.error('OEM is required');
            return;
        }
        if (!projectData.clientName.trim()) {
            toast.error('Client name is required');
            return;
        }
        if (!projectData.sqft || projectData.sqft <= 0) {
            toast.error('Valid square footage is required');
            return;
        }
        if (projectData.selectedEntities.length === 0) {
            toast.error('Please select at least one entity');
            // Scroll to entity section
            entitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (uploadedFiles.length === 0) {
            toast.error('Please upload at least one project document');
            // Scroll to documents section
            documentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (!projectData.preferredStartDate) {
            toast.error('Preferred start date is required');
            // Scroll to timeline section
            timelineSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (!projectData.preferredCompletionDate) {
            toast.error('Preferred completion date is required');
            // Scroll to timeline section
            timelineSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setLoading(true);

        try {
            // Create project with files in one call
            // Only send coordinates if site address is provided (both or neither)
            const hasAddress = projectData.siteAddress && projectData.siteAddress.trim() !== '';

            const projectPayload = {
                OemId: projectData.oemId || null,
                ClientName: projectData.clientName,
                ClientPhone: projectData.clientPhone || null,
                SiteAddress: hasAddress ? projectData.siteAddress : null,
                CityId: projectData.cityId || null,
                Pincode: projectData.pincode || null,
                Sqft: parseInt(projectData.sqft),
                Latitude: hasAddress && projectData.latitude ? parseFloat(projectData.latitude) : null,
                Longitude: hasAddress && projectData.longitude ? parseFloat(projectData.longitude) : null,
                PreferredStartDate: projectData.preferredStartDate || null,
                PreferredCompletionDate: projectData.preferredCompletionDate || null,
                EstimatedDays: projectEstimatedDays > 0 ? projectEstimatedDays : null,
                HasExtraWork: projectData.hasExtraWork,
                ExtraWorkNotes: projectData.extraWorkNotes || null,
                Entities: projectData.selectedEntities.map(e => ({
                    EntityId: e.entityId,
                    Quantity: e.quantity
                }))
            };

            // Single call with project data and files
            const response = await api.createProject(projectPayload, uploadedFiles);
            const newProjectId = response.data.id;

            toast.success('Project created successfully!');
            const dashboardPath = user?.role?.toLowerCase() === 'oem' ? '/oem/dashboard' : '/admin/dashboard';
            navigate(dashboardPath);
        } catch (error) {
            console.error('Error creating project:', error);
            // Error message is in error.message (not error.response) because api.js throws Error object
            toast.error(error.message || 'Failed to create project');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-6 flex items-center gap-4">
                    <Button
                        variant="outline"
                        onClick={() => navigate(user?.role?.toLowerCase() === 'oem' ? '/oem/dashboard' : '/admin/dashboard')}
                        className="flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Dashboard
                    </Button>
                    <h1 className="text-3xl font-bold text-gray-900">Create New Project</h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Basic Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {user?.role?.toLowerCase() !== 'oem' && (
                                    <div className="space-y-2">
                                        <Label htmlFor="oem">OEM *</Label>
                                        <Select
                                            value={projectData.oemId}
                                            onValueChange={(value) => setProjectData({ ...projectData, oemId: value })}
                                        >
                                            <SelectTrigger className="h-11">
                                                <SelectValue placeholder="Select OEM" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {oems.map(oem => (
                                                    <SelectItem key={oem.id} value={oem.id}>
                                                        {oem.companyName}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="clientName">Client Name *</Label>
                                    <Input
                                        id="clientName"
                                        value={projectData.clientName}
                                        onChange={(e) => setProjectData({ ...projectData, clientName: e.target.value })}
                                        placeholder="Enter client name"
                                        className="h-11"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="clientPhone">Client Phone</Label>
                                    <Input
                                        id="clientPhone"
                                        type="tel"
                                        value={projectData.clientPhone}
                                        onChange={(e) => setProjectData({ ...projectData, clientPhone: e.target.value })}
                                        placeholder="+91 9876543210"
                                        className="h-11"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="sqft">Square Footage *</Label>
                                    <Input
                                        id="sqft"
                                        type="number"
                                        value={projectData.sqft}
                                        onChange={(e) => setProjectData({ ...projectData, sqft: e.target.value })}
                                        placeholder="1200"
                                        min="1"
                                        className="h-11 [&::-webkit-inner-spin-button]:opacity-100 [&::-webkit-outer-spin-button]:opacity-100"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="city">City</Label>
                                    <Select
                                        value={projectData.cityId}
                                        onValueChange={(value) => setProjectData({ ...projectData, cityId: value })}
                                    >
                                        <SelectTrigger className="h-11">
                                            <SelectValue placeholder="Select city" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {cities.map(city => (
                                                <SelectItem key={city.id} value={city.id}>
                                                    {city.cityName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="pincode">Pincode</Label>
                                    <Input
                                        id="pincode"
                                        value={projectData.pincode}
                                        onChange={(e) => setProjectData({ ...projectData, pincode: e.target.value })}
                                        placeholder="400001"
                                        maxLength="6"
                                        className="h-11"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Project Entities */}
                    <Card ref={entitySectionRef}>
                        <CardHeader>
                            <CardTitle>Project Entities *</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-gray-600">
                                Select the entities included in this project and specify quantities
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {entities.map(entity => {
                                    const selected = projectData.selectedEntities.find(e => e.entityId === entity.id);
                                    return (
                                        <div
                                            key={entity.id}
                                            className={`border rounded-lg p-4 transition-all ${selected ? 'border-teal-500 bg-teal-50' : 'border-gray-200'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    id={`entity-${entity.id}`}
                                                    checked={!!selected}
                                                    onChange={() => toggleEntity(entity.id)}
                                                    className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1">
                                                            <label
                                                                htmlFor={`entity-${entity.id}`}
                                                                className="font-medium text-gray-900 cursor-pointer"
                                                            >
                                                                {entity.entityName}
                                                            </label>
                                                            {entity.description && (
                                                                <p className="text-xs text-gray-500 mt-1">{entity.description}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 ml-4">
                                                            <Label className="text-xs text-gray-600">Qty:</Label>
                                                            <div className="flex items-center border border-input rounded-md overflow-hidden">
                                                                <button
                                                                    type="button"
                                                                    disabled={!selected || (selected && selected.quantity <= 1)}
                                                                    onClick={() => updateEntityQuantity(entity.id, Math.max(1, (selected?.quantity || 1) - 1))}
                                                                    className="w-9 h-9 flex items-center justify-center text-lg font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                    aria-label="Decrease quantity"
                                                                >
                                                                    −
                                                                </button>
                                                                <Input
                                                                    type="number"
                                                                    inputMode="numeric"
                                                                    min="1"
                                                                    value={selected ? selected.quantity : 1}
                                                                    onChange={(e) => updateEntityQuantity(entity.id, e.target.value)}
                                                                    disabled={!selected}
                                                                    className="w-12 h-9 text-sm text-center border-0 rounded-none shadow-none focus-visible:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    disabled={!selected}
                                                                    onClick={() => updateEntityQuantity(entity.id, (selected?.quantity || 1) + 1)}
                                                                    className="w-9 h-9 flex items-center justify-center text-lg font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                    aria-label="Increase quantity"
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {projectData.selectedEntities.length === 0 && (
                                <p className="text-sm text-red-600">Please select at least one entity</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Timeline */}
                    <Card ref={timelineSectionRef}>
                        <CardHeader>
                            <CardTitle>Project Timeline</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="preferredStartDate">Preferred Start Date *</Label>
                                    <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="preferredStartDate"
                                                type="button"
                                                variant="outline"
                                                className="w-full justify-start text-left font-normal h-11"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {projectData.preferredStartDate
                                                    ? format(new Date(projectData.preferredStartDate + 'T00:00:00'), 'PPP')
                                                    : <span className="text-muted-foreground">Pick a date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarUI
                                                mode="single"
                                                selected={projectData.preferredStartDate ? new Date(projectData.preferredStartDate + 'T00:00:00') : undefined}
                                                onSelect={(date) => {
                                                    if (!date) return;
                                                    if (isHoliday(date)) {
                                                        toast.error(`${getHolidayName(date) || format(date, 'yyyy-MM-dd')} is a holiday`);
                                                        return;
                                                    }
                                                    setProjectData({ ...projectData, preferredStartDate: format(date, 'yyyy-MM-dd') });
                                                    setStartDateOpen(false);
                                                }}
                                                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0)) || isHoliday(date)}
                                                month={startCalendarMonth}
                                                onMonthChange={(month) => { setStartCalendarMonth(month); onHolidayMonthChange(month); }}
                                                modifiers={{ holiday: holidayDates }}
                                                modifiersClassNames={{ holiday: 'holiday-day' }}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="preferredCompletionDate">Preferred Completion Date *</Label>
                                    <Popover open={completionDateOpen} onOpenChange={setCompletionDateOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="preferredCompletionDate"
                                                type="button"
                                                variant="outline"
                                                className="w-full justify-start text-left font-normal h-11"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {projectData.preferredCompletionDate
                                                    ? format(new Date(projectData.preferredCompletionDate + 'T00:00:00'), 'PPP')
                                                    : <span className="text-muted-foreground">Pick a date</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarUI
                                                mode="single"
                                                selected={projectData.preferredCompletionDate ? new Date(projectData.preferredCompletionDate + 'T00:00:00') : undefined}
                                                onSelect={(date) => {
                                                    if (!date) return;
                                                    if (isHoliday(date)) {
                                                        toast.error(`${getHolidayName(date) || format(date, 'yyyy-MM-dd')} is a holiday`);
                                                        return;
                                                    }
                                                    setProjectData({ ...projectData, preferredCompletionDate: format(date, 'yyyy-MM-dd') });
                                                    setCompletionDateOpen(false);
                                                }}
                                                disabled={(date) => {
                                                    const minDate = projectData.preferredStartDate
                                                        ? new Date(projectData.preferredStartDate + 'T00:00:00')
                                                        : new Date(new Date().setHours(0, 0, 0, 0));
                                                    return date < minDate || isHoliday(date);
                                                }}
                                                month={completionCalendarMonth}
                                                onMonthChange={(month) => { setCompletionCalendarMonth(month); onHolidayMonthChange(month); }}
                                                modifiers={{ holiday: holidayDates }}
                                                modifiersClassNames={{ holiday: 'holiday-day' }}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>

                            {projectEstimatedDays > 0 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <p className="text-sm text-blue-800">
                                        📅 <strong>Estimated Duration:</strong> {projectEstimatedDays} day{projectEstimatedDays !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Extra Work */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Extra Work Requirements</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="hasExtraWork"
                                    checked={projectData.hasExtraWork}
                                    onChange={(e) => setProjectData({ ...projectData, hasExtraWork: e.target.checked, extraWorkNotes: e.target.checked ? projectData.extraWorkNotes : '' })}
                                    className="h-4 w-4"
                                />
                                <Label htmlFor="hasExtraWork" className="cursor-pointer">
                                    This project requires extra work
                                </Label>
                            </div>

                            {projectData.hasExtraWork && (
                                <div className="space-y-2">
                                    <Label htmlFor="extraWorkNotes">Extra Work Comments</Label>
                                    <textarea
                                        id="extraWorkNotes"
                                        value={projectData.extraWorkNotes}
                                        onChange={(e) => setProjectData({ ...projectData, extraWorkNotes: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg p-3 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Describe the extra work requirements..."
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* File Upload */}
                    <Card ref={documentsSectionRef}>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Upload className="w-5 h-5" />
                                Project Documents
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="file-upload">Upload Files <span className="text-red-500">*</span></Label>
                                <p className="text-sm text-gray-600 mb-2">At least one document is required</p>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                    <Input
                                        ref={fileInputRef}
                                        id="file-upload"
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        className="flex-1 min-w-0"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg,.xls,.xlsx"
                                    />
                                    <p className="text-xs text-gray-500">
                                        PDF, DOC, Images (JPG, PNG, GIF, WebP, BMP, SVG), Excel
                                    </p>
                                </div>
                            </div>

                            {uploadedFiles.length > 0 && (
                                <div className="space-y-2">
                                    <Label>Selected Files ({uploadedFiles.length})</Label>
                                    <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                                        {uploadedFiles.map((fileObj, index) => (
                                            <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 hover:bg-gray-50">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">
                                                            {fileObj.file.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {(fileObj.file.size / 1024).toFixed(2)} KB
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-end gap-2 sm:flex-shrink-0">
                                                    <div className="flex-1 sm:w-48 sm:flex-none">
                                                        <Label htmlFor={`subCategory-${index}`} className="text-xs">Document Type</Label>
                                                        <Input
                                                            id={`subCategory-${index}`}
                                                            type="text"
                                                            value={fileObj.subCategory}
                                                            onChange={(e) => {
                                                                const updated = [...uploadedFiles];
                                                                updated[index].subCategory = e.target.value;
                                                                setUploadedFiles(updated);
                                                            }}
                                                            placeholder="Enter document type..."
                                                            className="mt-1 text-sm"
                                                        />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeFile(index)}
                                                        className="flex-shrink-0"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Project Location */}
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <CardTitle className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5" />
                                    Project Location
                                </CardTitle>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClearLocation}
                                        disabled={!mapLoaded || (!projectData.latitude && !projectData.longitude)}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={getUserLocation}
                                        disabled={!mapLoaded}
                                    >
                                        <MapPin className="w-4 h-4 mr-1" />
                                        Use Live Location
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Site Address */}
                            <div className="space-y-2">
                                <Label htmlFor="siteAddress">Site Address</Label>
                                <Input
                                    id="siteAddress"
                                    className="h-11"
                                    value={projectData.siteAddress}
                                    onChange={(e) => setProjectData({ ...projectData, siteAddress: e.target.value })}
                                    placeholder="Enter project site address"
                                />
                            </div>

                            {/* Location Search */}
                            <div className="space-y-2">
                                <Label htmlFor="location-search">Search Location</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input
                                        id="location-search"
                                        placeholder="Search for a place..."
                                        className="pl-10"
                                        disabled={!mapLoaded}
                                    />
                                </div>
                                <p className="text-xs text-gray-500">
                                    Search for a location or click on the map to set project location
                                </p>
                            </div>

                            {/* Map Container */}
                            <div className="space-y-2">
                                <div className="relative">
                                    <div
                                        id="project-map"
                                        className="w-full h-96 rounded-lg border border-gray-300"
                                        style={{ minHeight: '400px' }}
                                    >
                                        {!mapLoaded && (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                                                <p className="text-gray-500">Loading map...</p>
                                            </div>
                                        )}
                                    </div>
                                    {/* Map Type Toggle Button (overlaid on map) */}
                                    {mapLoaded && (
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            className="absolute top-2 left-2 z-10 shadow-md"
                                            onClick={toggleMapType}
                                        >
                                            {mapType === 'roadmap' ? 'Satellite' : 'Roadmap'}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* GPS Coordinates */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="latitude">Latitude (Auto-filled)</Label>
                                    <Input
                                        id="latitude"
                                        value={projectData.latitude}
                                        placeholder="19.076090"
                                        readOnly
                                        className="h-11 bg-gray-50 cursor-not-allowed"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="longitude">Longitude (Auto-filled)</Label>
                                    <Input
                                        id="longitude"
                                        value={projectData.longitude}
                                        placeholder="72.877426"
                                        readOnly
                                        className="h-11 bg-gray-50 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Submit Buttons */}
                    <div className="flex items-center justify-end gap-4 pb-8">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/admin/dashboard')}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-teal-600 hover:bg-teal-700 text-white px-8"
                            disabled={loading}
                        >
                            {loading ? 'Creating Project...' : 'Create Project'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateProject;
